import { CheckboxField } from '@monorepo/shared/componentsV2/fields/CheckboxField';
import { getFieldNameWithPrefix } from '@monorepo/shared/componentsV2/fields/fieldUtils';
import {
  compose,
  isRequired,
  makeIsNoLongerThan,
  makeIsNotInList,
} from '@monorepo/shared/utils/validators';
import { MAX_FORM_TEMPLATE_FIELD_GROUP_NAME_LENGTH } from 'mapistry-shared';
import React, { useEffect } from 'react';
import { useField, useForm } from 'react-final-form';
import { useFormTemplateContext } from '../../EditFormTemplatePage/FormTemplateContext';
import { ModalFieldWrapper, ModalTextField } from '../styled';

type EditSectionFormProps = {
  currentName?: string;
  forceShowErrors?: boolean;
  isEditing?: boolean;
  prefix?: string;
};

export function EditSectionForm({
  currentName,
  forceShowErrors,
  isEditing,
  prefix,
}: EditSectionFormProps) {
  const {
    formTemplateHelpers: {
      activeGroupSlug,
      formTemplate,
      inspectionDateGroupSlug,
    },
  } = useFormTemplateContext();

  const otherGroupNames = formTemplate.groups
    .filter((group) => group.name !== currentName)
    .map((group) => group.name);

  const { change } = useForm();
  const {
    input: { value: isDuplicable },
  } = useField(getFieldNameWithPrefix('isDuplicable', prefix));

  const isStencilProtectedFieldName = getFieldNameWithPrefix(
    'isStencilProtected',
    prefix,
  );

  useEffect(() => {
    if (!isDuplicable) {
      change(isStencilProtectedFieldName, false);
    }
  }, [change, isDuplicable, isStencilProtectedFieldName]);

  const isTogglingDuplicableStateAllowed =
    !isEditing ||
    activeGroupSlug !== inspectionDateGroupSlug ||
    // allow correcting if section w/ inspection date is somehow duplicable
    isDuplicable;

  return (
    <>
      <ModalFieldWrapper>
        <ModalTextField
          forceShowErrors={forceShowErrors}
          label="Name"
          name={getFieldNameWithPrefix('name', prefix)}
          validate={compose(
            isRequired,
            makeIsNotInList(otherGroupNames, 'name'),
            makeIsNoLongerThan(MAX_FORM_TEMPLATE_FIELD_GROUP_NAME_LENGTH),
          )}
          required
        />
      </ModalFieldWrapper>
      <ModalFieldWrapper>
        <CheckboxField
          name={getFieldNameWithPrefix('isDuplicable', prefix)}
          label="Repeatable"
          helperText={`
              Use repeatable sections to repeat the same question set for each item
              you're inspecting (e.g., multiple tanks or outfalls).
            `}
          disabled={!isTogglingDuplicableStateAllowed}
          tooltip={
            isTogglingDuplicableStateAllowed
              ? ''
              : 'Repeatable sections cannot contain the inspection date. To make this section repeatable, ' +
                'you must first move the inspection date to another section.'
          }
          focusableWhenDisabled
        />
      </ModalFieldWrapper>
      <ModalFieldWrapper>
        <CheckboxField
          name={isStencilProtectedFieldName}
          label="Locked"
          helperText="Locks sub-sections added to the site’s form template so they cannot be deleted during inspections."
          disabled={!isDuplicable}
          tooltip={
            isDuplicable
              ? ''
              : 'Locked sections must be repeatable. Enable repeatable to lock this section.'
          }
          focusableWhenDisabled
        />
      </ModalFieldWrapper>
    </>
  );
}
